/**
 * Die sieben Linsen und alles, was sich aus ihren Urteilen ableitet.
 *
 * Reihenfolge hier = Reihenfolge im Linsen-Band (Q · M · E · C · L · I · S).
 * Sie ist ueberall dieselbe, damit man die "Form" einer Aktie ueber Seiten
 * hinweg wiedererkennt.
 */
export type LensKey = 'quality' | 'momentum' | 'epv' | 'contrarian' | 'lynch' | 'insider' | 'sentiment'

export interface LensMeta {
  key: LensKey
  code: string
  label: string
}

export const LENSES: readonly LensMeta[] = [
  { key: 'quality', code: 'Q', label: 'Quality' },
  { key: 'momentum', code: 'M', label: 'Momentum' },
  { key: 'epv', code: 'E', label: 'EPV' },
  { key: 'contrarian', code: 'C', label: 'Contrarian' },
  { key: 'lynch', code: 'L', label: 'Lynch' },
  { key: 'insider', code: 'I', label: 'Insider' },
  { key: 'sentiment', code: 'S', label: 'Sentiment' },
]

export const ALLE_LINSEN: LensKey[] = LENSES.map((l) => l.key)

export function linsenLabel(key: string): string {
  return LENSES.find((l) => l.key === key)?.label ?? key
}

/**
 * Unter dieser Abdeckung hat eine Linse kein Urteil, sondern eine Luecke.
 * Sie erscheint dann schraffiert als "kein Signal" -- NICHT als PASS. Ein
 * fehlendes Urteil ist kein schlechtes (Kims Punkt beim Insider-Fix).
 */
export const MIN_ZELL_ABDECKUNG = 0.5

export type ZellZustand = 'strong' | 'buy' | 'watch' | 'pass' | 'na'

export function zellZustand(urteil: string | undefined, abdeckung: number | undefined): ZellZustand {
  if (abdeckung === undefined || abdeckung === null || abdeckung < MIN_ZELL_ABDECKUNG) return 'na'
  switch (urteil) {
    case 'STRONG':
      return 'strong'
    case 'BUY':
      return 'buy'
    case 'WATCH':
      return 'watch'
    default:
      return 'pass'
  }
}

export const ZUSTAND_TEXT: Record<ZellZustand, string> = {
  strong: 'STRONG',
  buy: 'BUY',
  watch: 'WATCH',
  pass: 'PASS',
  na: 'kein Signal',
}

/** Obergrenze je Position, wie im Backend (lenses/sizing.py). */
export const POSITION_OBERGRENZE_PCT = 10

/**
 * Position % = Risikobudget % / Verlustannahme %, gedeckelt.
 *
 * Dieselbe Formel wie `sizing.max_position`. Das Backend liefert die
 * Verlustannahme mit -- damit laesst sich das Budget im Browser umstellen,
 * ohne das Universum neu zu bewerten (das kostet dort rund 34 Sekunden).
 */
export function positionFuer(
  verlustannahmePct: number | null | undefined,
  budgetPct: number,
  obergrenzePct: number = POSITION_OBERGRENZE_PCT,
): { pct: number | null; gedeckelt: boolean } {
  if (!verlustannahmePct || verlustannahmePct <= 0) return { pct: null, gedeckelt: false }
  const roh = (budgetPct / verlustannahmePct) * 100
  if (roh > obergrenzePct) return { pct: obergrenzePct, gedeckelt: true }
  return { pct: Math.round(roh * 100) / 100, gedeckelt: false }
}

const zahlDE = new Intl.NumberFormat('de-DE')

export function formatZahl(wert: number | null | undefined): string {
  return typeof wert === 'number' && Number.isFinite(wert) ? zahlDE.format(wert) : '–'
}

export function formatDezimal(wert: number | null | undefined, stellen = 1): string {
  if (typeof wert !== 'number' || !Number.isFinite(wert)) return '–'
  const text = wert.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen })
  // Echtes Minuszeichen statt Bindestrich: "−60 %" liest sich als Zahl, "-60 %" als Aufzaehlung.
  return text.replace(/^-/, '−')
}

export function formatDatum(wert: string | Date | null | undefined): string {
  if (!wert) return '–'
  const datum = typeof wert === 'string' ? new Date(wert.length === 10 ? `${wert}T12:00:00` : wert) : wert
  if (Number.isNaN(datum.getTime())) return '–'
  return datum.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function formatZeitpunkt(iso: string | null | undefined): string {
  if (!iso) return '–'
  const datum = new Date(iso)
  if (Number.isNaN(datum.getTime())) return '–'
  return datum.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function datumVorTagen(tage: number): Date {
  const d = new Date()
  d.setDate(d.getDate() - tage)
  return d
}

/** "Volatilitaet x 1.5" -> "Vola × 1,5" -- die Rohtexte kommen ASCII-kodiert. */
export function verlustBasisText(quelle: string | null | undefined): string {
  if (!quelle) return ''
  return quelle
    .replace('Volatilitaet', 'Vola')
    .replace('groesster', 'größter')
    .replace(/ x /g, ' × ')
    .replace(/(\d)\.(\d)/g, '$1,$2')
}
